import { useReactTable } from '@tanstack/react-table';
import React, { useCallback, useMemo, useState } from 'react';

import {
  CLINICAL_TASK_CATEGORIES,
  CLINICAL_TASK_CATEGORY_LABELS,
  CLINICAL_TASK_PRIORITIES,
  CLINICAL_TASK_PRIORITY_LABELS,
  CLINICAL_TASK_STATUS_LABELS,
} from '@eluve/clinical-tasks';
import type { DataTableFilterField } from '@eluve/components';
import { EditClinicalTaskSheet } from '@eluve/mini-apps';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';

import {
  ClinicalTasksTableView,
  buildTaskColumns,
} from './ClinicalTasksTableView';
import {
  type ClinicalTaskRow,
  TABLE_TASK_STATUSES,
} from './fetch-clinical-task-rows';
import { useClinicalTaskAssigneeOptions } from './useClinicalTaskAssigneeOptions';
import { useClinicalTaskMutations } from './useClinicalTaskMutations';
import { useClinicalTasksTableData } from './useClinicalTasksTableData';

/** Row → the shared edit sheet's field shape. Patient name is the sheet's
 *  read-only "For" row (bound to the encounter, never editable here). */
const toEditableFields = (row: ClinicalTaskRow) => ({
  description: row.description,
  category: row.category,
  priority: row.priority,
  status: row.status,
  dueDate: row.dueDate,
  ownerReference: row.ownerReference,
  forDisplay: row.patientName,
});

export const FilterableClinicalTasksTable: React.FC = () => {
  const tenantId = useAssignedTenantIdFromParams();
  const assigneeOptions = useClinicalTaskAssigneeOptions();

  const filterFields = useMemo<DataTableFilterField<ClinicalTaskRow>[]>(
    () => [
      {
        id: 'category',
        label: 'Type',
        options: CLINICAL_TASK_CATEGORIES.map((c) => ({
          label: CLINICAL_TASK_CATEGORY_LABELS[c],
          value: c,
        })),
      },
      {
        id: 'status',
        label: 'Status',
        options: TABLE_TASK_STATUSES.map((s) => ({
          label: CLINICAL_TASK_STATUS_LABELS[s],
          value: s,
        })),
      },
      {
        id: 'priority',
        label: 'Priority',
        options: CLINICAL_TASK_PRIORITIES.map((p) => ({
          label: CLINICAL_TASK_PRIORITY_LABELS[p],
          value: p,
        })),
      },
      { id: 'ownerReference', label: 'Assignee', options: assigneeOptions },
    ],
    [assigneeOptions],
  );

  const { rows, rowCount, reactTableOptions, isPending, isError, refetch } =
    useClinicalTasksTableData({ filterFields });
  const { practitioners, saveEdit, decide } =
    useClinicalTaskMutations(tenantId);

  const [openTaskId, setOpenTaskId] = useState<string | null>(null);
  // Re-read the open row from the latest fetch so it reflects saved edits.
  const openRow = useMemo(
    () => rows.find((r) => r.id === openTaskId) ?? null,
    [rows, openTaskId],
  );

  const columns = useMemo(
    () => buildTaskColumns((row) => setOpenTaskId(row.id)),
    [],
  );

  const table = useReactTable({
    ...reactTableOptions,
    data: rows,
    columns,
    rowCount,
    getRowId: (row) => row.id,
  });

  const closeSheet = useCallback(() => {
    setOpenTaskId(null);
    // The sheet autosaves to Medplum while open; pull the fresh page once.
    refetch();
  }, [refetch]);

  if (isError) {
    return (
      <p className="text-text-supporting">
        Failed to load tasks. Refresh the page to try again.
      </p>
    );
  }

  return (
    <>
      <ClinicalTasksTableView
        table={table}
        filterFields={filterFields}
        isPending={isPending}
        hasActiveFilters={
          (reactTableOptions.state?.columnFilters?.length ?? 0) > 0
        }
      />
      {openRow ? (
        // Reuses the appointment panel's edit slide-over (ENG-5243). A draft
        // shows Approve / Delete (routed through the review endpoint); an
        // accepted task shows a plain Done and autosaves browser-direct.
        <EditClinicalTaskSheet
          key={openRow.id}
          task={toEditableFields(openRow)}
          practitioners={practitioners}
          onSave={(edit) => saveEdit(openRow, edit)}
          onAccept={
            openRow.status === 'draft'
              ? () => decide(openRow, 'approve')
              : undefined
          }
          onDelete={
            openRow.status === 'draft'
              ? () => decide(openRow, 'reject')
              : undefined
          }
          onClose={closeSheet}
        />
      ) : null}
    </>
  );
};
